import React, { useEffect, useState } from "react";
import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Tabs,
  Tab,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { db } from "../firebase.config";
import { collection, getDocs, doc, getDoc, deleteDoc, query, where, addDoc } from "firebase/firestore";
import { useUserContext } from "./context/UserContext";
import { useNavigate } from 'react-router-dom';
import { generateDocumentPDF } from "./pdfGenerator";

const GeneratedDocuments = () => {
  const [invoices, setInvoices] = useState([]);
  const [invoiceType, setInvoiceType] = useState('tax'); // 'tax' or 'proforma'
  // Handler for invoice type toggle
  const handleInvoiceTypeChange = (event, newType) => {
    if (newType) setInvoiceType(newType);
  };
  const [quotations, setQuotations] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const { userId, isAdmin } = useUserContext();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchDocuments = async () => {
      try {
        const invoicesCollection = collection(db, "invoices");
        const quotationsCollection = collection(db, "quotations");

        let invoicesQuery = invoicesCollection;
        let quotationsQuery = quotationsCollection;

        if (!isAdmin) {
          invoicesQuery = query(invoicesCollection, where("user", "==", userId));
          quotationsQuery = query(quotationsCollection, where("user", "==", userId));
        }

        const [invoicesSnapshot, quotationsSnapshot] = await Promise.all([
          getDocs(invoicesQuery),
          getDocs(quotationsQuery),
        ]);

        const invoicesList = invoicesSnapshot.docs
          .filter(doc => doc.id !== "invoicecount")
          .map((doc) => {
            const data = doc.data();
            return {
              firestoreId: doc.id,
              ...data,
              type: "invoice",
              invoiceId: data.invoiceId || data.invoiceNumber || data.id || doc.id,
              total: data.total || 'N/A'
            };
          });

        setInvoices(invoicesList);

        const quotationsList = quotationsSnapshot.docs
          .filter(doc => doc.id !== "quotationcount")
          .map((doc) => {
            const data = doc.data();
            return {
              firestoreId: doc.id,
              ...data,
              type: "quotation",
              quotationId: data.quotationId || data.quotationNumber || data.id || doc.id,
              total: data.total || 'N/A'
            };
          });

        setQuotations(quotationsList);
      } catch (error) {
        console.error("Error fetching documents:", error);
      }
    };

    fetchDocuments();
  }, [userId, isAdmin]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleViewDocument = async (document) => {
    try {
      const docRef = doc(db, document.type === 'invoice' ? 'invoices' : 'quotations', document.firestoreId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        setSelectedDocument({ ...docSnap.data(), type: document.type });
        setOpenDialog(true);
      } else {
        console.log("No such document!");
      }
    } catch (error) {
      console.error("Error fetching document:", error);
    }
  };

  const handleDeleteDocument = async (document) => {
    if (window.confirm(`Are you sure you want to delete this ${document.type}?`)) {
      try {
        const docRef = doc(db, document.type === 'invoice' ? 'invoices' : 'quotations', document.firestoreId);
        await deleteDoc(docRef);
        
        // Update local state
        if (document.type === 'invoice') {
          setInvoices(invoices.filter(inv => inv.firestoreId !== document.firestoreId));
        } else {
          setQuotations(quotations.filter(quot => quot.firestoreId !== document.firestoreId));
        }
        
        console.log("Document deleted successfully");
      } catch (error) {
        console.error("Error deleting document:", error);
        alert("Failed to delete document. Please try again.");
      }
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedDocument(null);
  };

  // State and refs for download dialog and converted invoice
  const [showDownloadDialog, setShowDownloadDialog] = React.useState(false);
  const [convertedInvoice, setConvertedInvoice] = React.useState(null);
  const convertedInvoiceRef = React.useRef();

  // Handler for converting quotation to invoice
  const handleConvertToInvoice = (quotation) => {
    // Prepare invoice draft data from quotation
    let invoiceData = {
      ...quotation,
      type: 'invoice',
      invoiceId: quotation.quotationId || quotation.quotationNumber || quotation.id,
      date: quotation.quotationDate || new Date().toISOString().split('T')[0],
      items: quotation.items || [],
      total: quotation.total,
      subtotal: quotation.subtotal,
      gst: quotation.gst,
      billTo: quotation.billTo || quotation.customerInfo || {},
      shipTo: quotation.shipTo || {},
      businessName: quotation.businessName,
      businessAddress: quotation.businessAddress,
      authorizedSignature: quotation.authorizedSignature || '',
      authorizedSignatureName: quotation.authorizedSignatureName || '',
      customerSignature: quotation.customerSignature || '',
      customerSignatureName: quotation.customerSignatureName || '',
      templateId: 6, // Force to tax invoice template (if 6 is tax invoice)
    };
    // Remove quotation-only fields
    const removeFields = [
      'quotationId', 'quotationNumber', 'quotationDate', 'termsAndConditions',
      'coveringLetter', 'customFooter', 'type', 'firestoreId'
    ];
    removeFields.forEach(field => delete invoiceData[field]);
    invoiceData = Object.fromEntries(Object.entries(invoiceData).filter(([_, v]) => v !== undefined));
    // Navigate to InTemplate with draft
    navigate('/invoice-templates/InTemplate', { state: { draft: invoiceData } });
  };

  const renderTable = (documents, type) => {
    // For invoices, filter by invoiceType if present
    if (type === 'invoice') {
      documents = documents.filter(doc => {
        // Assume doc.invoiceCategory or doc.typeCategory or doc.invoiceType marks tax/proforma
        // Fallback: if not present, default to 'tax'
        const cat = (doc.invoiceCategory || doc.typeCategory || doc.invoiceType || 'tax').toLowerCase();
        return invoiceType === 'tax' ? cat === 'tax' : cat === 'proforma';
      });
    }
    const formatAmount = (amount) => {
      if (typeof amount === 'number' && !isNaN(amount)) {
        return `₹${amount.toFixed(2)}`;
      }
      if (typeof amount === 'string' && !isNaN(parseFloat(amount))) {
        return `₹${parseFloat(amount).toFixed(2)}`;
      }
      return 'N/A';
    };
    // Helper to calculate total from items if needed
    const getTotal = (doc) => {
      if (doc.total && !isNaN(parseFloat(doc.total))) {
        return parseFloat(doc.total);
      }
      // Try to calculate from items
      const items = doc.items || [];
      let subtotal = 0;
      let gst = 0;
      items.forEach(item => {
        const qty = parseFloat(item.quantity) || 0;
        const price = parseFloat(item.unitPrice) || 0;
        const discount = parseFloat(item.discount) || 0;
        const tax = parseFloat(item.tax) || 0;
        const itemTotal = qty * price * (1 - discount / 100);
        subtotal += itemTotal;
        gst += itemTotal * (tax / 100);
      });
      return subtotal + gst;
    };

    // Import the main templates for perfect match
    const QTemplate = require('./QTemplate').default;
    const InTemplate = require('./InTemplate').default;

    // Handler for editing Proforma Invoice
    const handleEditProformaInvoice = (invoice) => {
      // Redirect to invoice template page with invoice data in state
      navigate('/invoice-templates/InTemplate', { state: { draft: invoice } });
    };
    return (
      <>
        {type === 'invoice' && (
          <Box mb={2} display="flex" alignItems="center">
            <ToggleButtonGroup
              value={invoiceType}
              exclusive
              onChange={handleInvoiceTypeChange}
              aria-label="Invoice Type"
              size="small"
            >
              <ToggleButton value="tax" aria-label="Tax Invoice">Tax Invoice</ToggleButton>
              <ToggleButton value="proforma" aria-label="Proforma Invoice">Proforma Invoice</ToggleButton>
            </ToggleButtonGroup>
          </Box>
        )}
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{type === 'invoice' ? "Invoice ID" : "Quotation ID"}</TableCell>
                {type === 'invoice' && <TableCell>Type</TableCell>}
                <TableCell>Total Amount</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {documents.map((doc) => (
                <TableRow key={doc.firestoreId}>
                  <TableCell>
                    {type === 'invoice' ? doc.invoiceId : doc.quotationId}
                  </TableCell>
                  {type === 'invoice' && (
                    <TableCell>{(doc.invoiceCategory || doc.typeCategory || doc.invoiceType || 'Tax').toString().replace(/^[a-z]/, c => c.toUpperCase())} Invoice</TableCell>
                  )}
                  <TableCell>{formatAmount(getTotal(doc))}</TableCell>
                  <TableCell>
                    <IconButton
                      color="primary"
                      aria-label="View Details"
                      onClick={async () => {
                        await handleViewDocument(doc);
                      }}
                      sx={{ mr: type === 'quotation' ? 0.5 : 1 }}
                    >
                      <VisibilityIcon />
                    </IconButton>
                    {type === 'invoice' && (doc.invoiceType === 'proforma' || doc.invoiceType === 'Proforma') && (
                      <IconButton
                        color="info"
                        aria-label="Edit Proforma Invoice"
                        onClick={() => handleEditProformaInvoice(doc)}
                        sx={{ mr: 0.5 }}
                      >
                        <EditIcon />
                      </IconButton>
                    )}
                    {type === 'quotation' && (
                      <IconButton
                        color="secondary"
                        aria-label="Convert to Invoice"
                        onClick={() => handleConvertToInvoice(doc)}
                        sx={{ mr: 0.5 }}
                      >
                        <SwapHorizIcon />
                      </IconButton>
                    )}
                    <IconButton
                      color="error"
                      aria-label="Delete"
                      onClick={() => handleDeleteDocument(doc)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {/* Download PDF Dialog after conversion */}
        {showDownloadDialog && (
          <Dialog open={showDownloadDialog} onClose={() => setShowDownloadDialog(false)}>
            <DialogTitle>Invoice Converted!</DialogTitle>
            <DialogContent>
              <Typography gutterBottom>
                The quotation has been converted to an invoice. You can now download it as a PDF.
              </Typography>
              <Button
                variant="contained"
                color="primary"
                onClick={() => generateDocumentPDF({ type: 'invoice', data: convertedInvoice })}
                disabled={!convertedInvoice}
              >
                Download PDF
              </Button>
            </DialogContent>
          </Dialog>
        )}
      </>
    );
  };

  // PDF Preview as a React component to use hooks safely
  // Normalize document data for PDF generation
  function normalizeDocumentData(document, logoDataUrl) {
    if (!document) return {};
    
    // Calculate summary data for tax invoice
    const subtotal = document.subtotal || 0;
    const gstAmount = document.gst || document.totalTaxAmount || 0;
    const grandTotal = document.total || document.grandTotal || 0;
    const currencySymbol = document.currencySymbol || '₹';
    
    // Build summary array for the PDF generator
    const summary = [
      { label: 'Discount', value: `${document.discount || '0'}%` },
      { label: 'Taxable Amount', value: `${currencySymbol}${subtotal.toFixed(2)}` },
      { label: 'CGST', value: `${currencySymbol}${(gstAmount / 2).toFixed(2)}` },
      { label: 'SGST', value: `${currencySymbol}${(gstAmount / 2).toFixed(2)}` },
      { label: 'IGST', value: `${currencySymbol}0.00` },
      { label: 'Net Total', value: `${currencySymbol}${grandTotal.toFixed(2)}` },
    ];
    
    // Invoice/Quotation normalization
    return {
      ...document,
      businessName: document.businessName || document.companyName || '',
      businessAddress: document.businessAddress || document.companyAddress || '',
      invoiceNumber: document.invoiceId || document.invoiceNumber || '',
      invoiceDate: document.invoiceDate || document.date || '',
      quotationNumber: document.quotationId || document.quotationNumber || '',
      quotationDate: document.quotationDate || document.date || '',
      billTo: document.billTo || document.customerInfo || {},
      shipTo: document.shipTo || {},
      items: document.items || [],
      totals: document.totals || {
        subtotal: document.subtotal || 0,
        totalTaxAmount: document.gst || document.totalTaxAmount || 0,
        grandTotal: document.total || document.grandTotal || 0,
      },
      summary: summary,
      currencySymbol: document.currencySymbol || '₹',
      invoiceType: document.invoiceType || 'tax',
      authorizedSignature: document.authorizedSignature || '',
      authorizedSignatureName: document.authorizedSignatureName || '',
      customerSignature: document.customerSignature || '',
      customerSignatureName: document.customerSignatureName || '',
      customFooter: document.customFooter || '',
      termsAndConditions: document.termsAndConditions || '',
      additionalNotes: document.additionalNotes || '',
      coveringLetter: document.coveringLetter || '',
      logo: document.logo || logoDataUrl || '',
    };
  }

  function DocumentPDFPreview({ document }) {
    const [pdfUrl, setPdfUrl] = useState(null);
    const [logoDataUrl, setLogoDataUrl] = useState(null);
    // Load default logo if needed
    useEffect(() => {
      // Always use the logo from the document if present
      if (document && document.logo && document.logo.startsWith('data:image')) {
        setLogoDataUrl(document.logo);
      } else if (document && document.logo && document.logo.length > 0) {
        // If logo is a URL, try to fetch and convert to data URL
        fetch(document.logo)
          .then(res => res.blob())
          .then(blob => {
            const reader = new FileReader();
            reader.onloadend = () => setLogoDataUrl(reader.result);
            reader.readAsDataURL(blob);
          });
      } else {
        // Only use default if no logo at all
        fetch('/logo192.png')
          .then(res => res.blob())
          .then(blob => {
            const reader = new FileReader();
            reader.onloadend = () => setLogoDataUrl(reader.result);
            reader.readAsDataURL(blob);
          });
      }
    }, [document]);
    const normalized = normalizeDocumentData(document, logoDataUrl);
    useEffect(() => {
      let url = null;
      if (!logoDataUrl && (!document.logo || document.logo === '')) return;
      const docPdf = generateDocumentPDF({ type: document.type, ...normalized, returnBlob: true });
      if (docPdf instanceof Promise) {
        docPdf.then(blob => {
          url = URL.createObjectURL(blob);
          setPdfUrl(url);
        });
      } else {
        url = URL.createObjectURL(docPdf);
        setPdfUrl(url);
      }
      return () => { if (url) URL.revokeObjectURL(url); };
    }, [document, logoDataUrl]);
    return (
      <Box>
        <Button
          variant="contained"
          color="primary"
          onClick={() => generateDocumentPDF({ type: document.type, ...normalized })}
          sx={{ mb: 2 }}
        >
          Download PDF
        </Button>
        {pdfUrl && (
          <iframe
            src={pdfUrl}
            title="PDF Preview"
            width="100%"
            height="600px"
            style={{ border: '1px solid #ccc', borderRadius: 4 }}
          />
        )}
      </Box>
    );
  }

  // Use the new component in the dialog
  const renderDocumentDetails = (document) => <DocumentPDFPreview document={document} />;

  const handleDeleteAll = async () => {
    const currentDocs = activeTab === 0 ? invoices : quotations;
    const docType = activeTab === 0 ? 'invoice' : 'quotation';
    const collectionName = activeTab === 0 ? 'invoices' : 'quotations';
    
    if (currentDocs.length === 0) {
      alert(`No ${docType}s to delete.`);
      return;
    }
    
    if (window.confirm(`Are you sure you want to delete ALL ${currentDocs.length} ${docType}s? This action cannot be undone!`)) {
      try {
        // Delete all documents
        const deletePromises = currentDocs.map(document => 
          deleteDoc(doc(db, collectionName, document.firestoreId))
        );
        
        await Promise.all(deletePromises);
        
        // Update local state
        if (activeTab === 0) {
          setInvoices([]);
        } else {
          setQuotations([]);
        }
        
        alert(`All ${docType}s deleted successfully!`);
      } catch (error) {
        console.error("Error deleting documents:", error);
        alert("Failed to delete some documents. Please try again.");
      }
    }
  };

  return (
    <Box p={4}>
      <Box display="flex" justifyContent="flex-start" alignItems="center" mb={2}>
        <Typography variant="h4">
          Generated Documents
        </Typography>
      </Box>
      <Tabs value={activeTab} onChange={handleTabChange}>
        <Tab label="Invoices" />
        <Tab label="Quotations" />
      </Tabs>

      <Box mt={2}>
        {activeTab === 0
          ? renderTable(invoices, 'invoice')
          : renderTable(quotations, 'quotation')}
      </Box>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          {selectedDocument?.type === 'invoice' ? 'Invoice' : 'Quotation'} Details
          <IconButton
            aria-label="close"
            onClick={handleCloseDialog}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedDocument && renderDocumentDetails(selectedDocument)}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default GeneratedDocuments;